// src/components/GeographicDistribution.js
// Admin analytics panel: students by country, segmented by course status, plus
// a distinct "Trial leads" segment for courseless children with an OPEN trial.
//
// The status segments are data-driven — they come from `availableStatuses`
// (discovered from real data upstream), so any new status renders automatically
// with a fallback color and nothing breaks. Click a country bar to drill into
// its states/provinces. Childless accounts (staff/empty) are excluded, as is
// any account with nothing renderable at all (see renderable filter below).
//
// Stage 5 audit: a trial student is a LEAD, not a student. Before this pass, a
// courseless child rendered as course.status "none" — an unlabelled bucket that
// silently counted leads as students. Two changes:
//   1. `totalStudents` (the summary line) now counts ONLY children with a
//      course — exact, matching the "Total Students" fix on AdminDashboard.
//   2. A courseless child with an OPEN trial (requested/scheduled/held/
//      no_show — see openTrialStudentIds, sourced from AdminDashboard's fetch
//      of the `trials` collection) gets its own segment, LEAD_SEGMENT_KEY,
//      distinctly coloured and legended as "Trial leads" — never folded into a
//      course status. A courseless child with a CLOSED trial (won/lost/
//      cancelled/expired) or no trial doc at all (an orphan — see the Phase 2
//      recon) is dropped from the chart entirely, on purpose: this is a
//      SNAPSHOT of the current pipeline, not a historical record of where
//      leads came from. Keeping closed leads would make the segment accumulate
//      dead leads forever and stop meaning anything. A historical "where did
//      our leads come from" chart is a different, trials-collection-sourced
//      view — Phase 3.
import React, { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Globe, ArrowLeft } from 'lucide-react';

const UNKNOWN_LABEL = 'Unknown';
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const LEAD_SEGMENT_KEY = 'trial_lead';
const LEAD_SEGMENT_LABEL = 'Trial leads';
// Indigo — not used by any course-status color, so it never reads as a shade
// of an existing status (e.g. 'paused' is amber elsewhere in this app; a lead
// is a different kind of thing and needs a genuinely distinct color here).
const LEAD_SEGMENT_COLOR = '#6366f1';

function GeographicDistribution({ users = [], availableStatuses = [], statusColor, openTrialStudentIds }) {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const openLeads = openTrialStudentIds instanceof Set ? openTrialStudentIds : new Set();

  const resolveColor = (status) => {
    if (status === LEAD_SEGMENT_KEY) return LEAD_SEGMENT_COLOR;
    return (typeof statusColor === 'function' ? statusColor(status) : null) || '#cbd5e1';
  };
  const segmentLabel = (status) => (status === LEAD_SEGMENT_KEY ? LEAD_SEGMENT_LABEL : capitalize(status));

  const {
    countryRows, stateRowsByCountry, totalAccounts, accountsWithLocation, totalStudents, totalLeads,
  } = useMemo(() => {
    const countries = {};
    const statesByCountry = {};
    let totalAccounts = 0;
    let accountsWithLocation = 0;
    let totalStudents = 0;
    let totalLeads = 0;

    users.forEach((user) => {
      const kids = user.children ?? [];

      // Renderable = has a course (any status), OR is a courseless child whose
      // trial is still open. Everything else (no children at all, a closed
      // trial, an orphan with no trial doc) contributes nothing to this chart.
      const renderable = kids.filter((child) => !!child.course || (child.id && openLeads.has(child.id)));
      if (renderable.length === 0) return;

      totalAccounts += 1;
      const rawCountry = (user.country || '').trim();
      if (rawCountry) accountsWithLocation += 1;
      const country = rawCountry || UNKNOWN_LABEL;
      const state = (user.stateProvince || '').trim() || UNKNOWN_LABEL;

      if (!countries[country]) countries[country] = { country, _total: 0, _families: 0 };
      countries[country]._families += 1;

      if (!statesByCountry[country]) statesByCountry[country] = {};
      if (!statesByCountry[country][state]) {
        statesByCountry[country][state] = { state, _total: 0, _families: 0 };
      }
      statesByCountry[country][state]._families += 1;

      renderable.forEach((child) => {
        const status = child.course ? (child.course.status || 'none') : LEAD_SEGMENT_KEY;
        if (status === LEAD_SEGMENT_KEY) totalLeads += 1;
        else totalStudents += 1;
        countries[country][status] = (countries[country][status] || 0) + 1;
        countries[country]._total += 1;
        statesByCountry[country][state][status] =
          (statesByCountry[country][state][status] || 0) + 1;
        statesByCountry[country][state]._total += 1;
      });
    });

    const countryRows = Object.values(countries).sort((a, b) => b._total - a._total);
    const stateRowsByCountry = {};
    Object.entries(statesByCountry).forEach(([c, states]) => {
      stateRowsByCountry[c] = Object.values(states).sort((a, b) => b._total - a._total);
    });

    return { countryRows, stateRowsByCountry, totalAccounts, accountsWithLocation, totalStudents, totalLeads };
  }, [users, openLeads]);

  // Status keys that drive the stacked segments: discovered statuses first (in
  // their upstream priority order), then any extras found in the rows
  // (e.g. 'none' for malformed data, or LEAD_SEGMENT_KEY when leads exist).
  const chartStatuses = useMemo(() => {
    const ordered = [...availableStatuses];
    const present = new Set();
    countryRows.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (k !== 'country' && k !== '_total' && k !== '_families') present.add(k);
      });
    });
    present.forEach((s) => { if (!ordered.includes(s)) ordered.push(s); });
    return ordered;
  }, [availableStatuses, countryRows]);

  const activeRows = selectedCountry ? (stateRowsByCountry[selectedCountry] || []) : countryRows;
  const xKey = selectedCountry ? 'state' : 'country';
  const coveragePct = totalAccounts > 0
    ? Math.round((accountsWithLocation / totalAccounts) * 100) : 0;

  const handleBarClick = (data) => {
    if (selectedCountry) return;
    const country = data?.country ?? data?.payload?.country;
    if (country && country !== UNKNOWN_LABEL) setSelectedCountry(country);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe size={20} className="text-purple-600" />
          <h3 className="text-lg font-bold text-gray-800">
            {selectedCountry ? `${selectedCountry} — by State/Province` : 'Students by Country'}
          </h3>
        </div>
        {selectedCountry && (
          <button
            onClick={() => setSelectedCountry(null)}
            className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-semibold"
          >
            <ArrowLeft size={14} />
            All countries
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-4">
        {totalStudents} student{totalStudents === 1 ? '' : 's'}
        {totalLeads > 0 && (
          <span className="text-indigo-500"> · {totalLeads} trial lead{totalLeads === 1 ? '' : 's'}</span>
        )}
        {' '}across {countryRows.length} location{countryRows.length === 1 ? '' : 's'} · location data on{' '}
        {coveragePct}% of accounts ({accountsWithLocation}/{totalAccounts})
        {!selectedCountry && countryRows.length > 0 && ' · click a bar to drill into states/provinces'}
      </p>

      {activeRows.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No location data yet.</div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(220, activeRows.length * 38)}>
          <BarChart data={activeRows} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} />
            <YAxis type="category" dataKey={xKey} width={140} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {chartStatuses.map((status) => (
              <Bar
                key={status}
                dataKey={status}
                name={segmentLabel(status)}
                stackId="students"
                fill={resolveColor(status)}
                cursor={selectedCountry ? 'default' : 'pointer'}
                onClick={handleBarClick}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default GeographicDistribution;
